import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ReservedResourcePhase, ResourceOccupancyFragment } from 'src/api/api.generated'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { displayGuestsCount } from 'src/utils/reservation'
import { ReservedResourceF } from '../../../types/reservation-detail.types'
import { displayResourceType } from '../../../../../../../utils/resource-type'

export type ResourceRow = {
  type: 'DATA' | 'LOADING'
  resource: { id: string; code: string } | null | undefined
}

export type RR = {
  id: string
  index: number
  propertyId: string
  resource: ResourceRow
  resourceType: string
  resourceTypeId: string
  assignedResourceType?: string
  assignedResourceTypeId?: string
  phase: ReservedResourcePhase
  guestCount: string
  rate: string
  chargeCategoryCode: string
  rateId: string
  start: Date
  end: Date
  currentResourceOccupancy: ResourceOccupancyFragment
  adults: number
  children: {
    childId: string
    quantity: number
  }[]
  totalPrice: number
  isTimeResource: boolean
  time?: { from: Date; to: Date }
}

export type ReservedResourceRow = RR & {
  editable: boolean
}

export function useReservedResourceRow() {
  const { formatTableCurrency } = useFormatCurrency()
  const localizeString = useLocalizeString()
  const { t } = useTranslation()
  const toReservedResourceRow: (rr: ReservedResourceF) => RR = useCallback(
    (rr: ReservedResourceF): RR => {
      const occ = rr.currentResourceOccupancy
      const resource: ResourceRow = {
        type: 'DATA',
        resource: occ?.resource || null,
      }

      return {
        id: rr.id,
        index: rr.index,
        propertyId: rr.propertyId,
        resource: resource,
        resourceType: displayResourceType(localizeString, occ.reservedResourceType),
        resourceTypeId: occ.reservedResourceType.id,
        assignedResourceType: occ.assignedResourceType ? displayResourceType(localizeString, occ.assignedResourceType) : undefined,
        assignedResourceTypeId: occ.assignedResourceType?.id,
        phase: rr.phase,
        guestCount: displayGuestsCount(t, rr.adults, rr.children),
        rate: localizeString(rr.rate.name),
        chargeCategoryCode: rr.chargeCategory.code ?? '',
        rateId: rr.rate.id,
        start: rr.start,
        end: rr.end,
        currentResourceOccupancy: occ,
        adults: rr.adults,
        children: rr.childOccupancies?.map((c) => ({ childId: c.child.id, quantity: c.quantity })) ?? [],
        totalPrice: rr.price,
        isTimeResource: occ.assignedResourceType.timeConfiguration ? true : false,
        time: rr.time ? { from: rr.time.from, to: rr.time.to } : undefined,
      }
    },
    [localizeString, displayGuestsCount],
  )

  return toReservedResourceRow
}
